import { useQuery } from '@tanstack/react-query'
import type { DocumentDef } from '@project/contracts'
import { useApi, type DocumentSummary } from '../app/api.js'

export const useDocumentMenu = () => {
  const api = useApi()
  return useQuery({
    queryKey: ['menu', api.companyId],
    queryFn: () => api.documentMenu(),
    staleTime: 5 * 60_000,
  })
}

export interface ResolvedDocument {
  readonly summary: DocumentSummary | undefined
  readonly def: DocumentDef | undefined
  readonly isLoading: boolean
  readonly error: unknown
  readonly missing: boolean
}

/**
 * The URL carries the route segment (`pur_ords`) while the metadata endpoint is
 * keyed by document key (`purchase_order`); the menu is the map between them and
 * is cached for the session.
 */
export const useDocumentByRoute = (docRoute: string): ResolvedDocument => {
  const api = useApi()
  const menu = useDocumentMenu()
  const summary = menu.data?.find((d) => d.route === docRoute)
  const def = useQuery({
    queryKey: ['def', api.companyId, summary?.key],
    queryFn: () => api.documentDef(summary?.key ?? ''),
    enabled: Boolean(summary?.key),
    staleTime: 5 * 60_000,
  })
  return {
    summary,
    def: def.data,
    isLoading: menu.isLoading || def.isLoading,
    error: menu.error ?? def.error,
    missing: !menu.isLoading && !menu.error && summary === undefined,
  }
}

/**
 * The company's settings, cached for the session.
 *
 * A setting decides whether a column is on a grid at all, so it is fetched
 * once and shared rather than read per component.
 */
export const useSettings = () => {
  const api = useApi()
  return useQuery({
    queryKey: ['settings', api.companyId],
    queryFn: () => api.settings(),
    staleTime: 60_000,
  })
}
